/*
 * qi-minqty-sort.js — "Min Order Qty" / "Max Order Qty" collection sorting
 * -----------------------------------------------------------------------
 * WHY THIS IS CLIENT-SIDE
 *   Minimum order quantity lives in the `minqty.tier1` product metafield, and
 *   Shopify's sort_by has no key for a metafield — so the platform cannot order
 *   the collection for us. The dropdown therefore carries our choice as
 *   ?qi_sort=qi-minqty-ascending|descending (never sort_by, which Shopify
 *   would reject) and we re-order the grid ourselves.
 *
 * WHY IT FETCHES EVERY PAGE
 *   A collection is paginated 20 at a time (179 mugs = 9 pages). Sorting only
 *   the visible page would LOOK right while ignoring ~90% of the products —
 *   "lowest minimum order" would really mean "lowest on this page". So we pull
 *   every page, sort the full set, render it, and hide the now-meaningless
 *   pagination. If any fetch fails we degrade to sorting the current page and
 *   say so, rather than silently presenting a partial result as complete.
 *
 * DATA CONTRACT (emitted by snippets/product-grid-item.liquid)
 *   .grid-uniform > .grid-item[data-minqty][data-handle]
 *   data-minqty = integer, or -1 when the product has no minqty metafield.
 *   -1 sorts LAST in both directions — a missing value must never read as
 *   "lowest minimum order".
 *
 * Only touches the collection grid. Featured/top-product rows live in their own
 * containers and are left alone.
 */
(function () {
  'use strict';

  var PARAM = 'qi_sort';
  var ASC = 'qi-minqty-ascending';
  var DESC = 'qi-minqty-descending';
  var MAX_PAGES = 25;          // runaway guard (~500 products)
  var ITEM_SEL = '.grid-item[data-minqty]';

  function param(name) {
    var m = new RegExp('[?&]' + name + '=([^&]*)').exec(location.search);
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : null;
  }

  function grid() {
    var grids = document.querySelectorAll('.grid-uniform');
    for (var i = 0; i < grids.length; i++) {
      if (grids[i].querySelector(ITEM_SEL)) return grids[i];
    }
    return null;
  }

  function qtyOf(node) {
    var n = parseInt(node.getAttribute('data-minqty'), 10);
    return isFinite(n) ? n : -1;
  }

  // -1 (unknown) always sinks to the bottom, whichever direction is chosen
  function comparator(dir) {
    return function (a, b) {
      var qa = qtyOf(a), qb = qtyOf(b);
      if (qa < 0 && qb < 0) return 0;
      if (qa < 0) return 1;
      if (qb < 0) return -1;
      return dir === DESC ? qb - qa : qa - qb;
    };
  }

  /* ------------------------------------------------------------ page count */

  function lastPage() {
    var max = 1;
    var links = document.querySelectorAll('a[href*="page="]');
    for (var i = 0; i < links.length; i++) {
      var m = /[?&]page=(\d+)/.exec(links[i].getAttribute('href') || '');
      if (m) { var p = parseInt(m[1], 10); if (isFinite(p) && p > max) max = p; }
    }
    return Math.min(max, MAX_PAGES);
  }

  function pageUrl(n) {
    var u = location.pathname + location.search;
    u = u.replace(/([?&])page=\d+/, '$1page=' + n);
    if (!/[?&]page=\d+/.test(u)) u += (u.indexOf('?') > -1 ? '&' : '?') + 'page=' + n;
    return u;
  }

  /* --------------------------------------------------------------- status */

  function status(text, busy) {
    var el = document.getElementById('qi-sort-status');
    // nothing to say and nothing on screen — don't inject an empty node
    if (!el && !text) return;
    if (!el) {
      var g = grid();
      if (!g) return;
      el = document.createElement('div');
      el.id = 'qi-sort-status';
      el.className = 'qi-sort-status';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      g.parentNode.insertBefore(el, g);
    }
    el.textContent = text || '';
    el.classList.toggle('is-busy', !!busy);
    el.style.display = text ? '' : 'none';
  }

  function hidePagination() {
    var pags = document.querySelectorAll('.pagination-border-top, .pagination');
    for (var i = 0; i < pags.length; i++) pags[i].style.display = 'none';
  }

  /* ----------------------------------------------------------------- sort */

  function render(g, items) {
    var frag = document.createDocumentFragment();
    for (var i = 0; i < items.length; i++) frag.appendChild(items[i]);
    // wipe only the product cards; keep any non-card children the theme injected
    var existing = g.querySelectorAll(ITEM_SEL);
    for (var j = 0; j < existing.length; j++) existing[j].parentNode.removeChild(existing[j]);
    g.appendChild(frag);
  }

  // `note` is only passed on the degraded path — a successful sort stays silent
  function sortCurrentPageOnly(g, dir, note) {
    var items = [].slice.call(g.querySelectorAll(ITEM_SEL));
    items.sort(comparator(dir));
    render(g, items);
    status(note || '', false);
  }

  function fetchPage(n) {
    return fetch(pageUrl(n), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return [].slice.call(doc.querySelectorAll('.grid-uniform ' + ITEM_SEL));
      });
  }

  function run(dir) {
    var g = grid();
    if (!g) return;

    var pages = lastPage();
    var onPage = [].slice.call(g.querySelectorAll(ITEM_SEL));

    if (pages <= 1) {
      sortCurrentPageOnly(g, dir);
      return;
    }

    // silent feedback while the pages load — a text banner reads as clutter
    g.classList.add('qi-sorting');

    var jobs = [];
    for (var p = 1; p <= pages; p++) jobs.push(fetchPage(p));

    Promise.all(jobs).then(function (chunks) {
      g.classList.remove('qi-sorting');
      var seen = {}, all = [];
      for (var i = 0; i < chunks.length; i++) {
        for (var j = 0; j < chunks[i].length; j++) {
          var node = chunks[i][j];
          var key = node.getAttribute('data-handle') || (node.textContent || '').slice(0, 40);
          if (seen[key]) continue;      // pagination overlap / duplicate safety
          seen[key] = 1;
          all.push(document.importNode(node, true));
        }
      }
      if (!all.length) { sortCurrentPageOnly(g, dir); return; }

      all.sort(comparator(dir));
      render(g, all);
      hidePagination();
      status('', false);   // sorted fine — no banner
    }).catch(function () {
      g.classList.remove('qi-sorting');
      // never present a partial sort as if it were the whole collection
      sortCurrentPageOnly(g, dir,
        'Could not load every page — sorted only the ' + onPage.length +
        ' products shown here. Refresh to try again.');
    });
  }

  function init() {
    var dir = param(PARAM);
    if (dir !== ASC && dir !== DESC) return;
    if (!window.fetch || !window.DOMParser || !window.Promise) {
      var g = grid();
      if (g) sortCurrentPageOnly(g, dir);
      return;
    }
    run(dir);
  }

  window.QIMinQtySort = { run: run, init: init };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
